import React, { ReactNode, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { COLORS_BASE, STYLES } from '../../utils/consts';

type ContainerProps = {
  children: ReactNode,
  flex?: boolean,
  padding?: string,
  margin?: string,
  height?: string,
  width?: string,
  shadow?: boolean,
  backgroundColor?: string,
  color?: string,
  border?: string,
  fontWeight?: string,
  maxWidth?: string,
  textAlign?: string,
  justifyContent?: string,
  alignItems?: string,
  flexDirection?: string,
  className?: string,
  transition?: boolean,
  borderRadius?: string,
}

function Container({
  children,
  flex,
  padding,
  height,
  width,
  margin,
  shadow,
  color,
  backgroundColor,
  border,
  fontWeight,
  maxWidth,
  textAlign,
  justifyContent,
  alignItems,
  flexDirection,
  className,
  borderRadius,
  transition = true,
}: ContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <ContainerStyle
      ref={containerRef}
      flex={flex} 
      padding={padding} 
      margin={margin} 
      height={height} 
      width={width} 
      shadow={shadow} 
      color={color} 
      backgroundColor={backgroundColor}
      border={border} 
      fontWeight={fontWeight}
      maxWidth={maxWidth}
      textAlign={textAlign}
      justifyContent={justifyContent}
      alignItems={alignItems}
      flexDirection={flexDirection}
      className={className}
      transition={transition}
      borderRadius={borderRadius}
    >
      {children}
    </ContainerStyle>
  )
}

export default Container;

const ContainerStyle = styled.div<{ 
  flex?: boolean,
  padding?: string, 
  margin?: string, 
  height?: string, 
  width?: string; 
  shadow?: boolean; 
  color?: string;
  backgroundColor?: string; 
  border?: string;
  fontWeight?: string;
  maxWidth?: string;
  textAlign?: string;
  justifyContent?: string;
  alignItems?: string;
  flexDirection?: string;
  borderRadius?: string;
  transition?: boolean;
 }>(
  ({ flex = false, 
    padding = '15px', 
    height = 'inherit', 
    width = 'auto', 
    margin = '10px', 
    shadow = false, 
    color = "inherit", 
    border = STYLES.BORDER_STYLE,
    fontWeight = "normal",
    maxWidth = "auto",
    backgroundColor = "white",
    textAlign = "left",
    justifyContent = "flex-start",
    alignItems = "stretch",
    flexDirection = "row",
    borderRadius = "5px",
    transition = true
  }) => `
  color: ${color};
  font-weight: ${fontWeight};
  background-color: ${backgroundColor};
  height:  ${height};
  width:  ${width};
  margin: ${margin};
  padding: ${padding};
  border: ${border};
  border-radius: ${borderRadius};
  pointer-events: auto;
  text-align: ${textAlign};
  ${transition ? `
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 2s ease-out, transform 2s ease-out;
    &.visible {
      opacity: 1;
      transform: translateY(0);
    }
  ` : `
    opacity: 1;
    transform: none;
    transition: none;
  `}
  ${shadow ? `
    box-shadow: 0px 0px 20px 0px rgba(0,0,0,0.2);
    -webkit-box-shadow: 0px 0px 20px 0px rgba(0,0,0,0.2);
    -moz-box-shadow: 0px 0px 20px 0px rgba(0,0,0,0.2);
  `: ''}
  ${flex ? `
    display: flex;
    justify-content: ${justifyContent};
    align-items: ${alignItems};
    flex-direction: ${flexDirection};`: ''}
`)